import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import StableInterviewPage from '../modules/interview/pages/InterviewPage';
import screeningService from '../services/screeningService';
import { InterviewFeedback } from '../types/interviews';
import { CheckCircle, XCircle, BarChart2, Sparkles } from 'lucide-react';
import { ParticleBackground } from '../components/ParticleBackground';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import { Button } from '../components/common/Button';
import { Logo } from '../components/common/Logo';

interface ScreeningSessionState {
  token: string;
  candidate?: Record<string, any>;
  interviewDetails: any;
  status: string;
  callbackUrl?: string;
  webhookToken?: string;
}

const MinimalNavbar: React.FC = () => (
  <header className="sticky top-0 z-50 bg-white/70 dark:bg-surface-dark/40 backdrop-blur-xl border-b border-white/40 dark:border-white/10">
    <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
      <Logo showText={true} onClick={() => window.open('https://platform.mocktrail.app')} />
      <div className="">
      <Button
        variant="primary"
        size="sm"
        className='opacity-0 md:opacity-100 md:py-3 md:px-5'
        leftIcon={<Sparkles size={14} />}
        onClick={() => window.open('https://platform.mocktrail.app', '_blank', 'noopener,noreferrer')}
        >
          Practice & Improve
        </Button>
      </div>
    </div>
  </header>
);

// Format duration as hh:mm:ss like if secondsUsed is 127, it should be 2m 7s
const formatDuration = (secondsUsed: number) => {
  console.log('[ScreeningInterviewPage] Seconds used:', secondsUsed);
  if (secondsUsed <= 60) return '<1m';

  const hours = Math.floor(secondsUsed / 3600);
  const minutes = Math.floor((secondsUsed % 3600) / 60);
  const seconds = secondsUsed % 60;

  console.log('hours:', hours);
  console.log('minutes:', minutes);
  console.log('seconds:', seconds);
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  parts.push(`${minutes}m ${seconds}s`);

  return parts.join(' ');
};

const ScreeningInterviewPage: React.FC = () => {
  const { id: token } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  const [session, setSession] = useState<ScreeningSessionState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [feedback, setFeedback] = useState<InterviewFeedback | null>(null);

  const backgroundGradient = useMemo<React.CSSProperties>(() => ({
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative',
    zIndex: 0,
  }), []);

  const isPopup = typeof window !== 'undefined' && !!window.opener;

  const renderResultLayout = useCallback((content: React.ReactNode, showNavbar: boolean = false) => (
    <div
      className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark"
      style={backgroundGradient}
    >
      {showNavbar && <MinimalNavbar />}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow" />
        <div
          className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow"
          style={{ animationDelay: '1s' }}
        />
        <div
          className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow"
          style={{ animationDelay: '2s' }}
        />
      </div>
      <ParticleBackground />
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 pt-14 pb-16">
        <PageTransitionItem index={1}>
          <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-4 md:p-6 max-w-2xl w-full text-center backdrop-blur-sm shadow-sm">
            {content}
          </div>
        </PageTransitionItem>
      </div>
    </div>
  ), [backgroundGradient]);

  useEffect(() => {
    if (isCompleted) {
      document.title = 'Screening Result | MockTrail';
    }
  }, [isCompleted]);

  useEffect(() => {
    let isMounted = true;

    if (!token) {
      setLoadError('Invalid interview link.');
      setIsLoading(false);
      return;
    }

    const fetchSession = async () => {
      try {
        setIsLoading(true);
        const sessionData = await screeningService.fetchSession(token);
        if (sessionData && sessionData.interviewDetails) {
          const resumePayload =
            sessionData.interviewDetails?.resume ??
            sessionData.interviewDetails?.selectedResume ??
            sessionData.candidate?.resume ??
            sessionData.candidate?.selectedResume ??
            null;
          const resumeId =
            sessionData.interviewDetails?.resumeId ?? sessionData.candidate?.resumeId ?? null;

          sessionData.interviewDetails = {
            ...sessionData.interviewDetails,
            ...(sessionData.callbackUrl
              ? {
                  callbackUrl: sessionData.callbackUrl,
                  webhookToken:
                    sessionData.webhookToken ?? sessionData.interviewDetails?.webhookToken,
                }
              : {}),
            ...(resumePayload
              ? {
                  resume: resumePayload,
                  selectedResume: resumePayload,
                }
              : {}),
            ...(resumeId ? { resumeId } : {}),
          };
        }
        if (!isMounted) return;
        setSession(sessionData);
      } catch (error: any) {
        console.error('[ScreeningInterviewPage] Failed to fetch session:', error);
        if (!isMounted) return;
        setLoadError('Unable to load interview details. Please contact the provider for a new link.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSession();

    return () => {
      isMounted = false;
    };
  }, [token]);

  // Once session data is available, replace state so InterviewPage receives the prepared details
  useEffect(() => {
    if (!session) return;
    navigate(location.pathname, {
      replace: true,
      state: {
        interviewDetails: session.interviewDetails,
        userInterviewId: session.token,
      },
    });
  }, [session, location.pathname, navigate]);

  const handleInterviewComplete = useCallback(async (
    _interviewId: string,
    _seriesId: string | undefined,
    feedbackApplicable: boolean,
    feedbackData?: InterviewFeedback | null,
  ) => {
    if (!token) return;

    if (feedbackApplicable && feedbackData) {
      setFeedback(feedbackData);
      try {
        await screeningService.submitFeedback(token, { feedback: feedbackData });
      } catch (error) {
        console.error('[ScreeningInterviewPage] Failed to submit screening feedback:', error);
      }

      try {
        window.parent?.postMessage({
          type: 'mocktrailScreeningFeedback',
          token,
          feedback: feedbackData,
        }, '*');
      } catch (postMessageError) {
        console.warn('[ScreeningInterviewPage] Unable to postMessage feedback to parent:', postMessageError);
      }
    }

    setIsCompleted(true);
    sessionStorage.removeItem('interview_session_id');
  }, [token]);

  const candidateName = useMemo(() => {
    if (!session?.candidate) return 'Candidate';
    return session.candidate.name || session.candidate.fullName || 'Candidate';
  }, [session]);

  useEffect(() => {
    if (!isCompleted) return;
    const timer = setTimeout(() => {
      try {
        if (window.opener || window.parent !== window) {
          window.close();
        }
      } catch (error) {
        console.warn('[ScreeningInterviewPage] Unable to close window automatically:', error);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isCompleted]);

  if (isLoading) {
    return renderResultLayout(
      <>
        <div className="mx-auto mb-6 h-14 w-14 animate-spin rounded-full border-4 border-primary-light/20 border-t-primary-light dark:border-primary-dark/20 dark:border-t-primary-dark" />
        <h1 className="text-xl font-semibold mb-3">Preparing your interview…</h1>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Hang tight while we set up your MockTrail screening experience.
        </p>
      </>,
      false
    );
  }

  if (loadError || !session || !token) {
    return renderResultLayout(
      <>
        <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
        </div>
        <h1 className="text-2xl font-semibold mb-3">Interview unavailable</h1>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
          {loadError || 'The screening link may have expired or been used already. Please contact the team that shared it for a fresh link.'}
        </p>
        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => window.open('https://platform.mocktrail.app', '_blank', 'noopener,noreferrer')}>
            Go to Dashboard
          </Button>
        </div>
      </>,
      false
    );
  }

  if (isCompleted) {
    const interviewDetails = feedback?.interviewDetails;
    const overallScoreValue = typeof feedback?.overallScore === 'number' ? feedback.overallScore : null;
    const scoreToneClass = overallScoreValue != null
      ? overallScoreValue >= 80
        ? 'text-green-600'
        : overallScoreValue >= 60
          ? 'text-blue-600'
          : overallScoreValue >= 40
            ? 'text-yellow-600'
            : 'text-red-600'
      : 'text-blue-600';

    const durationSeconds = (() => {
      if (typeof interviewDetails?.minutesUsed === 'number' && interviewDetails.minutesUsed > 0) {
        return interviewDetails.minutesUsed;
      }
      if (typeof interviewDetails?.duration === 'number' && interviewDetails.duration > 0) {
        return interviewDetails.duration * 60;
      }
      return null;
    })();

    const interviewMeta: Array<{ label: string; value?: string | number | null }> = [
      { label: 'Job Role', value: interviewDetails?.jobRole !== null && interviewDetails?.jobRole !== undefined && interviewDetails?.jobRole?.length > 45 ? interviewDetails?.jobRole?.substring(0,45) + '...': interviewDetails?.jobRole},
      { label: 'Company', value: interviewDetails?.company },
      {
        label: 'Duration',
        value: durationSeconds != null ? formatDuration(durationSeconds) : null,
      },
      { label: 'Language', value: interviewDetails?.languageName || interviewDetails?.language },
      {
        label: 'Completed On',
        value: interviewDetails?.updatedAt
          ? new Date(interviewDetails.updatedAt).toLocaleString()
          : interviewDetails?.scheduledAt
            ? new Date(interviewDetails.scheduledAt).toLocaleString()
            : null,
      },
    ].filter((item) => item.value);

    return renderResultLayout(
      <>
        <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
        </div>
        <h1 className="text-3xl md:text-2xl font-semibold mb-4 md:mb-3">Thank You For Interviewing!</h1>
        <p className="text-base md:text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
          Your screening interview performance has been <strong className='text-text-primary-light dark:text-text-primary-dark '>recorded and shared</strong> with the company.<br></br>You can now close this tab & go back to the job page.
        </p>
        <div className="mt-8 w-full text-left space-y-6">
          {interviewMeta.length > 0 && (
            <div className="bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-xl p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {interviewMeta.map((item) => (
                  <div key={item.label}>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">{item.label}</p>
                    <p className="font-medium text-text-primary-light dark:text-text-primary-dark font-heading text-base">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {overallScoreValue != null && (
            <div className="rounded-xl border border-border-light dark:border-border-dark bg-background-subtle-light/80 dark:bg-background-subtle-dark/60 p-5">
              <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                <BarChart2 className="mr-2 h-5 w-5 text-primary-light dark:text-primary-dark" />
                Overall Performance
              </h4>
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex flex-col gap-1 items-center justify-center">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full" viewBox="0 0 120 120">
                      <circle
                        className="text-border-light/60 dark:text-border-dark/40 stroke-current"
                        strokeWidth="10"
                        cx="60"
                        cy="60"
                        r="48"
                        fill="transparent"
                      />
                      <circle
                        className={`${scoreToneClass} stroke-current`}
                        strokeWidth="10"
                        strokeLinecap="round"
                        cx="60"
                        cy="60"
                        r="48"
                        fill="transparent"
                        strokeDasharray={`${2 * Math.PI * 48} ${2 * Math.PI * 48}`}
                        strokeDashoffset={2 * Math.PI * 48 - (overallScoreValue / 100) * (2 * Math.PI * 48)}
                        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-3xl font-bold ${scoreToneClass}`}>{overallScoreValue}%</span>
                    </div>
                  </div>
                  <p className='text-sm'>Your Score</p>
                </div>
                {feedback?.summary && (
                  <div className="flex-1 bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-lg p-4 text-sm text-text-primary-light dark:text-text-primary-dark leading-relaxed">
                    <span className='font-semibold'>Summary:</span><br></br>
                    {feedback.summary}
                  </div>
                )}
              </div>
            </div>
          )}
          {feedback?.summary && overallScoreValue == null && (
            <div className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl p-5">
              <p className="text-xs uppercase tracking-wide text-text-tertiary-light dark:text-text-tertiary-dark mb-2">Summary</p>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark leading-relaxed">
                {feedback.summary}
              </p>
            </div>
          )}
        </div>
        {isPopup && (
          <p className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark mt-6">
            This window will automatically close in a few seconds.
          </p>
        )}
      </>,
      true
    );
  }

  return (
    <StableInterviewPage
      videoRef={videoRef}
      videoStream={videoStream}
      onInterviewComplete={handleInterviewComplete}
      onVideoStreamChange={setVideoStream}
      userData={{
        name: candidateName,
        profileImage: session.candidate?.avatarUrl,
        interviewGoals: [],
        minutesLeft: 999,
      }}
    />
  );
};

export default withPageTransition(ScreeningInterviewPage);
